/**
 * Utilitários para construir URLs do Supabase Storage
 *
 * O campo avatar_url (e demais paths de mídia) no banco armazena apenas o
 * caminho relativo (ex: "Chats/uuid/avatar.jpg"). Esta função constrói a
 * URL completa do storage usando o domínio configurado em VITE_SUPABASE_URL
 * (que pode ser o domínio próprio, ex.: https://api.uaizychat.com.br).
 *
 * Formato do caminho: Chats/{contact_id}/avatar.{ext}
 * URL completa: {VITE_SUPABASE_URL}/storage/v1/object/public/anexos/{path}
 *
 * CHANGELOG:
 *   v1.2.0 (2026-06-13) - URLs absolutas do Supabase Storage agora são
 *     normalizadas para o host configurado em VITE_SUPABASE_URL. Antes,
 *     qualquer string começando com http(s):// era devolvida crua, fazendo
 *     com que anexos legados gravados com o host bruto do Supabase
 *     (https://<ref>.supabase.co/storage/...) abrissem fora do domínio
 *     próprio do tenant. Hosts externos reais (CDN de terceiros, etc.) são
 *     preservados como antes.
 *   v1.1.0 - Adicionada função getThumbnailUrl para otimização de imagens
 */

// URL base do Supabase - usa variável de ambiente para facilitar troca de ambientes
// (em produção costuma apontar para o domínio próprio, ex.: https://api.uaizychat.com.br)
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

// Marcadores que identificam uma URL como sendo do Supabase Storage (qualquer
// projeto/host). Quando encontrados em uma URL absoluta, reescrevemos o host
// para o configurado em VITE_SUPABASE_URL preservando todo o restante.
const STORAGE_PATH_MARKERS = [
  '/storage/v1/object/public/',
  '/storage/v1/render/image/public/',
  '/storage/v1/object/sign/',
];

/**
 * Converte um caminho relativo do storage para URL completa.
 * Também normaliza URLs absolutas que já apontem para o Supabase Storage,
 * reescrevendo o host para o configurado em VITE_SUPABASE_URL (domínio próprio).
 *
 * @param path - Caminho relativo (ex: "Chats/abc123/avatar.jpg"), URL absoluta
 *               do Supabase Storage (legado) ou URL externa real.
 * @returns URL completa do storage no domínio próprio, URL externa preservada,
 *          ou undefined se path for null/undefined.
 */
export function getStorageUrl(path: string | null | undefined): string | undefined {
  // Se não tem path, retorna undefined
  if (!path) return undefined;

  // Caminho local absoluto (começa com /) — usado para arquivos em public/
  // como imagens de documentação. Mantém intacto.
  if (path.startsWith('/')) {
    return path;
  }

  // URL absoluta (http/https)
  if (path.startsWith('http://') || path.startsWith('https://')) {
    // Verifica se é do Supabase Storage; se sim, reescreve o host para
    // VITE_SUPABASE_URL preservando path + querystring.
    const marker = STORAGE_PATH_MARKERS.find((m) => path.includes(m));
    if (marker && SUPABASE_URL) {
      const tailStart = path.indexOf(marker);
      return `${SUPABASE_URL}${path.substring(tailStart)}`;
    }
    // URL externa real (CDN de terceiro, avatar público, etc.) — mantém como está.
    return path;
  }

  // Caminho relativo — monta URL pública no bucket "anexos"
  return `${SUPABASE_URL}/storage/v1/object/public/anexos/${path}`;
}

/**
 * Gera o caminho de storage para um avatar de contato
 *
 * @param contactId - UUID do contato
 * @param extension - Extensão do arquivo (default: jpg)
 * @returns Caminho no formato "Chats/{contactId}/avatar.{ext}"
 */
export function getAvatarStoragePath(contactId: string, extension: string = 'jpg'): string {
  return `Chats/${contactId}/avatar.${extension}`;
}

/**
 * Gera URL otimizada para thumbnail usando transformação do Supabase Storage
 * Reduz drasticamente o tamanho das requisições para miniaturas
 *
 * Usa o endpoint /render/image/ do Supabase que suporta transformações on-the-fly:
 * - width: largura máxima
 * - height: altura máxima
 * - resize: modo de redimensionamento (cover, contain, fill)
 *
 * @param url - URL completa da imagem ou null/undefined
 * @param width - Largura desejada (default: 128)
 * @param height - Altura desejada (default: 128)
 * @returns URL com transformação ou original se não aplicável
 */
export function getThumbnailUrl(
  url: string | null | undefined,
  width: number = 128,
  height: number = 128
): string | undefined {
  // Se não tem URL, retorna undefined
  if (!url) return undefined;

  // Só aplica transformação para URLs do Supabase Storage
  if (!url.includes('/storage/v1/object/public/')) {
    return url;
  }

  // Substituir /object/public/ por /render/image/public/ para usar transformação
  const transformedUrl = url.replace(
    '/storage/v1/object/public/',
    '/storage/v1/render/image/public/'
  );

  // Adicionar parâmetros de redimensionamento
  const separator = transformedUrl.includes('?') ? '&' : '?';
  return `${transformedUrl}${separator}width=${width}&height=${height}&resize=cover`;
}
